import type { SimulationPlatform } from '@lensora/shared'
import { cn } from '@lensora/ui'
import { X, Hash } from 'lucide-react'
import * as React from 'react'

import { api } from '../../lib/api'

import { FaviconImg } from './FaviconImg'
import { formatPlatformName } from './PlatformIcon'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface DrawerCompetitor {
  competitor_id: string
  name: string
  domain: string
  favicon_url: string
  mention_rate: number
  rank: number
}

interface CompetitorPrompt {
  query_text: string
  platforms: string[]
  position: number | null
  scanned_at: string
}

interface Props {
  competitor: DrawerCompetitor | null
  onClose: () => void
}

// ── Platform pill ──────────────────────────────────────────────────────────────

// Pill accent colours only — mirrors the automation page's PLATFORM_ACCENT.
// Typed against every SimulationPlatform so a new platform fails to compile
// here until it's given a colour, instead of silently falling through to the
// generic grey pill (which is what happened to 'google-ai' before this).
const PLATFORM_ACCENT: Record<SimulationPlatform, { color: string; bg: string }> = {
  chatgpt:          { color: '#000000', bg: '#f1f5f9' },
  claude:           { color: '#C96442', bg: 'rgba(201,100,66,0.1)' },
  perplexity:       { color: '#20B8CD', bg: 'rgba(32,184,205,0.1)' },
  gemini:           { color: '#4285F4', bg: 'rgba(66,133,244,0.1)' },
  'google-ai':      { color: '#34A853', bg: 'rgba(52,168,83,0.1)' },
  'chatgpt-search': { color: '#0EA5E9', bg: 'rgba(14,165,233,0.1)' },
  'claude-search':  { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
}

function getPlatformStyle(platform: string): { color: string; bg: string; label: string } {
  const key = platform.toLowerCase().replace(/\s/g, '') as SimulationPlatform
  const accent = Object.hasOwn(PLATFORM_ACCENT, key) ? PLATFORM_ACCENT[key] : undefined
  return { ...(accent ?? { color: '#64748b', bg: '#f8fafc' }), label: formatPlatformName(platform) }
}

// ── Main component ─────────────────────────────────────────────────────────────

export function CompetitorDrawer({ competitor, onClose }: Props) {
  const [prompts, setPrompts] = React.useState<CompetitorPrompt[]>([])
  const [loading, setLoading] = React.useState(false)
  const open = competitor !== null

  React.useEffect(() => {
    if (!competitor) return
    setLoading(true)
    setPrompts([])
    api
      .get<{ prompts: CompetitorPrompt[] }>(`/api/competitors/${competitor.competitor_id}/prompts`)
      .then((data) => setPrompts(data.prompts ?? []))
      .catch(() => setPrompts([]))
      .finally(() => setLoading(false))
  }, [competitor?.competitor_id])

  React.useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/50 transition-opacity duration-200',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed right-0 top-0 z-50 flex h-dvh w-full max-w-lg flex-col overflow-hidden border-l border-border bg-surface shadow-xl transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="shrink-0 border-b border-border px-6 py-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              {competitor && (
                <FaviconImg
                  domain={competitor.domain}
                  name={competitor.name}
                  size={40}
                  className="mt-0.5 shrink-0"
                />
              )}
              <div className="min-w-0">
                <p className="truncate text-lg font-bold text-text">{competitor?.name}</p>
                <p className="truncate text-sm text-text-muted">{competitor?.domain}</p>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  {competitor && (
                    <>
                      <span className="rounded-full bg-brand-accent/10 px-2.5 py-1 text-[11px] font-medium text-brand-accent">
                        #{competitor.rank} ranked
                      </span>
                      <span className="rounded-full bg-feedback-success/10 px-2.5 py-1 text-[11px] font-medium text-feedback-success">
                        {competitor.mention_rate}% visibility
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 rounded-lg p-1.5 text-text-muted transition-colors hover:bg-surface-overlay hover:text-text"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Section label */}
        <div className="shrink-0 px-6 py-3">
          <p className="text-[11px] font-medium uppercase tracking-widest text-text-muted">
            Prompts they appear in
          </p>
        </div>

        {/* Scrollable list */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg bg-surface-overlay" />
              ))}
            </div>
          ) : prompts.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <p className="text-sm text-text-muted">No prompt data yet for this competitor.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {prompts.map((prompt, i) => (
                <PromptRow key={i} prompt={prompt} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function PromptRow({ prompt }: { prompt: CompetitorPrompt }) {
  return (
    <div className="rounded-lg border border-border bg-surface-raised p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="flex-1 text-sm leading-relaxed text-text">{prompt.query_text}</p>
        {prompt.position !== null && (
          <div className="flex shrink-0 items-center gap-0.5">
            <Hash className="h-3.5 w-3.5 text-text-muted" />
            <span className="text-sm font-bold text-brand-accent">{prompt.position}</span>
          </div>
        )}
      </div>
      {prompt.platforms.length > 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {prompt.platforms.map((platform) => {
            const cfg = getPlatformStyle(platform)
            return (
              <span
                key={platform}
                className="rounded-full px-2 py-0.5 text-[11px] font-medium"
                style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}
              >
                {cfg.label}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}
